import {
  SubChecklistCard,
  Subscriber,
  FullStatistics,
  SubscriberPage,
  SubscribersPerGroupParams,
  SubscribersPerEnrollmentParams,
  ProductInfo,
  EeClass,
  SubscriberAddOnMethod,
  SubscribersSortBy,
  SubscribersSortOrder,
  SubscribersFilter,
  ElectionStatus,
  RequiredSubInfo,
  PersonInfo,
  QuotingStatistics,
  EnrollsUsers,
} from 'types/electionService';
import { FetchResult } from '@apollo/client';
import apolloClient from 'api/apolloClient';
import {
  ADD_OR_UPDATE_DEPENDENT,
  APPLY_ELECTION_CHANGE_REASON,
  CAN_COMPLETE_ADD_EMPLOYEES,
  COMPLETE_CHECKLIST_STEP,
  COMPLETE_ELECTION_CHANGE_STEP,
  GET_ENROLLS_BY_USER_ID,
  GET_REQUIRED_SUB_INFO,
  GET_STATISTIC,
  GET_SUBSCRIBER_BY_ID,
  GET_SUBSCRIBER_BY_ID_WITH_CHOICES,
  GET_SUBSCRIBER_BY_USER_ID_AND_GROUP_ID,
  GET_SUBSCRIBERS_PAGE_FOR_GROUP,
  GET_SUBSCRIBER_PAGE_FOR_ENROLLMENT,
  REMOVE_DEPENDENT,
  REMOVE_SUBSCRIBER,
  UPDATE_EMAIL,
  UPDATE_SUBSCRIBER,
  UPDATE_SUBSCRIBER_INFO,
  GET_POSSIBLE_CHOICE,
  GET_EMPLOYEE_CLASSES,
  CREATE_EMPLOYEES_FOR_GROUP,
  ADD_FULL_EMPLOYEE_TO_GROUP,
  GET_SUBSCRIBER_PAGE,
  UPDATE_SUBSCRIBERS,
  REMIND_COMPLETE_PROFILE,
  CAN_COMPLETE_QUOTING_ADD_EMPLOYEES,
  GET_QUOTING_STATISTIC,
  INVITE_ALL,
  CLOSE_QUOTING_TASKS,
  APPLY_NO_CONDITIONS,
  SAVE_GENERAL_IMQ_ANSWERS,
  SAVE_IMQ_CATEGORY_ANSWERS,
  SAVE_IMQ_MEDICAL_CONDITION_ANSWERS,
  REMOVE_IMQ_MEDICAL_CONDITION_MEMBER,
  GET_IMQ_REQUIRED_SUB_INFO,
  GET_SUBSCRIBER_BY_ID_WITH_IMQ,
} from 'api/queries/electionService';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { Primitive } from 'types/general';
import { IMQAnswers, RequiredSubInfoWithIMQ, SubscriberIMQ } from 'store/modules/subscriber/subscriber-imq/types';

interface CompleteSubscriberChecklistStepInput {
  subId: string;
  entityId: string;
  card: SubChecklistCard;
}

export async function getSubscribers(
  groupId: string,
  periodId: string,
  sortBy: SubscribersSortBy,
  sortOrder: SubscribersSortOrder,
  filter: SubscribersFilter,
  searchValue: string,
  electionStatuses: ElectionStatus[],
  employeeClasses: string[],
  page: number,
): Promise<SubscriberPage> {
  return (
    await apolloClient.query<{ getSubscriberPage: SubscriberPage }>({
      query: GET_SUBSCRIBER_PAGE,
      fetchPolicy: 'no-cache',
      variables: {
        groupId,
        periodId,
        sortBy,
        sortOrder,
        filter,
        name: searchValue,
        electionStatuses,
        employeeClasses,
        page,
      },
    })
  ).data.getSubscriberPage;
}

export async function getSubscriberById(id: string): Promise<Subscriber> {
  return (
    await apolloClient.query<{ getSubscriberById: Subscriber }>({
      query: GET_SUBSCRIBER_BY_ID,
      fetchPolicy: 'no-cache',
      variables: { id },
    })
  ).data.getSubscriberById;
}

export async function getIMQSubscriberById(id: string): Promise<SubscriberIMQ> {
  return (
    await apolloClient.query<{ getSubscriberById: SubscriberIMQ }>({
      query: GET_SUBSCRIBER_BY_ID_WITH_IMQ,
      fetchPolicy: 'no-cache',
      variables: { id },
    })
  ).data.getSubscriberById;
}

export async function getSubscriberWithChoices(id: string, periodId: string): Promise<Subscriber> {
  return (
    await apolloClient.query<{ getSubscriberWithChoices: Subscriber }>({
      query: GET_SUBSCRIBER_BY_ID_WITH_CHOICES,
      fetchPolicy: 'no-cache',
      variables: { id, periodId },
    })
  ).data.getSubscriberWithChoices;
}

export async function getSubscriberByUserIdAndGroupId(groupId: string): Promise<Subscriber | null> {
  return (
    await apolloClient.query<{ getSubscriberByUserIdAndGroupId: Subscriber | null }>({
      query: GET_SUBSCRIBER_BY_USER_ID_AND_GROUP_ID,
      fetchPolicy: 'no-cache',
      variables: { groupId },
    })
  ).data.getSubscriberByUserIdAndGroupId;
}

export async function getEnrollsByUserId(): Promise<EnrollsUsers> {
  return (
    await apolloClient.query<{ getEnrollsByUserId: EnrollsUsers }>({
      query: GET_ENROLLS_BY_USER_ID,
      fetchPolicy: 'no-cache',
    })
  ).data.getEnrollsByUserId;
}

export async function completeElectionChangeStep(subId: string, card: SubChecklistCard): Promise<void> {
  await apolloClient.mutate({
    mutation: COMPLETE_ELECTION_CHANGE_STEP,
    variables: { subId, card },
  });
}

export async function applyElectionChangeReason(
  domainId: string,
  subId: string,
  reason: string,
  date: string,
): Promise<void> {
  await apolloClient.mutate({
    mutation: APPLY_ELECTION_CHANGE_REASON,
    variables: { domainId, subId, reason, date },
  });
}

export async function updateSubscribers(periodId: string, subscribers: Subscriber[]): Promise<void> {
  await apolloClient.mutate<{ updateSubscribers: Subscriber[] }>({
    mutation: UPDATE_SUBSCRIBERS,
    variables: { periodId, subscribers },
  });
}

export async function getStatistics(groupId: string, periodId: string): Promise<FullStatistics> {
  return (
    await apolloClient.query<{ getStatistic: FullStatistics }>({
      query: GET_STATISTIC,
      fetchPolicy: 'no-cache',
      variables: { groupId, periodId },
    })
  ).data.getStatistic;
}

export async function getQuotingStatistics(groupId: string, cartId: string): Promise<QuotingStatistics> {
  return (
    await apolloClient.query<{ getQuotingStatistic: QuotingStatistics }>({
      query: GET_QUOTING_STATISTIC,
      fetchPolicy: 'no-cache',
      variables: { groupId, cartId },
    })
  ).data.getQuotingStatistic;
}

export async function getSubscriberPageForEnrollment({
  groupId,
  periodId,
  sortBy,
  sortOrder,
  filter,
  status,
  employeeClasses,
  progress,
  name = '',
  page = 0,
}: SubscribersPerEnrollmentParams): Promise<SubscriberPage> {
  return (
    await apolloClient.mutate<{ getSubscriberPageForEnrollment: SubscriberPage }>({
      mutation: GET_SUBSCRIBER_PAGE_FOR_ENROLLMENT,
      variables: { groupId, periodId, sortBy, sortOrder, filter, status, employeeClasses, progress, name, page },
      fetchPolicy: 'no-cache',
    })
  ).data!.getSubscriberPageForEnrollment;
}

export async function getSubscribersPageForGroup({
  groupId,
  cartId,
  sortBy,
  sortOrder,
  filter,
  status,
  addOnMethod,
  progress,
  name = '',
  page = 0,
}: SubscribersPerGroupParams): Promise<SubscriberPage> {
  return (
    await apolloClient.mutate<{ getSubscribersPageForGroup: SubscriberPage }>({
      mutation: GET_SUBSCRIBERS_PAGE_FOR_GROUP,
      variables: { groupId, cartId, sortBy, sortOrder, filter, status, addOnMethod, progress, name, page },
    })
  ).data!.getSubscribersPageForGroup;
}

/** Full-document subscriber write (elections). Prefer atomic APIs when available. */
export async function updateSubscriber(periodId: string, subscriber: Subscriber): Promise<void> {
  await apolloClient.mutate<{ updateSubscriber: Subscriber }>({
    mutation: UPDATE_SUBSCRIBER,
    variables: { periodId, subscriber },
  });
}

/** Atomic primary (common) info update. Returns updated subscriber slice for remapping. */
export async function updateSubscriberInfo(
  subId: string,
  info: Record<string, string>,
): Promise<Pick<Subscriber, 'id' | 'common' | 'custom'>> {
  const res = await apolloClient.mutate<{ updateSubscriberInfo: Pick<Subscriber, 'id' | 'common' | 'custom'> }>({
    mutation: UPDATE_SUBSCRIBER_INFO,
    variables: { subId, info },
  });
  return res.data!.updateSubscriberInfo;
}

export async function removeSubscriber(id: string): Promise<void> {
  await apolloClient.mutate({
    mutation: REMOVE_SUBSCRIBER,
    variables: { id },
  });
}

export async function addOrUpdateDependent(
  subscriberId: string,
  dependent: PersonInfo,
): Promise<FetchResult<{ addOrUpdateDependent: string }>> {
  return apolloClient.mutate({
    mutation: ADD_OR_UPDATE_DEPENDENT,
    variables: { subscriberId, dependent },
  });
}

export async function removeSubscriberDependent(subscriberId: string, dependentId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: REMOVE_DEPENDENT,
    variables: { subscriberId, dependentId },
  });
}

export async function completeSubscriberChecklistStep({
  subId,
  entityId,
  card,
}: CompleteSubscriberChecklistStepInput): Promise<boolean> {
  const res = await apolloClient.mutate<{ completeChecklistStep: boolean }>({
    mutation: COMPLETE_CHECKLIST_STEP,
    variables: { subId, entityId, card },
  });

  return res.data!.completeChecklistStep;
}

export async function updateEmail(subId: string, email: string): Promise<void> {
  await apolloClient.mutate({
    mutation: UPDATE_EMAIL,
    variables: { subId, email },
  });
}

export async function getRequiredSubInfo(domainId?: string): Promise<RequiredSubInfo> {
  return (
    await apolloClient.query<{ getRequiredSubInfo: RequiredSubInfo }>({
      query: GET_REQUIRED_SUB_INFO,
      fetchPolicy: 'no-cache',
      variables: { domainId },
    })
  ).data.getRequiredSubInfo;
}

export async function getIMQRequiredSubInfo(domainId?: string): Promise<RequiredSubInfoWithIMQ> {
  return (
    await apolloClient.query<{ getRequiredSubInfo: RequiredSubInfoWithIMQ }>({
      query: GET_IMQ_REQUIRED_SUB_INFO,
      fetchPolicy: 'no-cache',
      variables: { domainId },
    })
  ).data.getRequiredSubInfo;
}

export async function canCompleteAddEmployees(groupId: string, periodId: string): Promise<boolean> {
  return (
    await apolloClient.query<{ canCompleteAddEmployees: boolean }>({
      query: CAN_COMPLETE_ADD_EMPLOYEES,
      variables: { groupId, periodId },
      fetchPolicy: 'no-cache',
    })
  ).data.canCompleteAddEmployees;
}

export async function canCompleteQuotingAddEmployees(groupId: string): Promise<boolean> {
  return (
    await apolloClient.query<{ canCompleteQuotingAddEmployees: boolean }>({
      query: CAN_COMPLETE_QUOTING_ADD_EMPLOYEES,
      variables: { groupId },
      fetchPolicy: 'no-cache',
    })
  ).data.canCompleteQuotingAddEmployees;
}

export async function getProducts(periodId: string): Promise<ProductInfo[]> {
  const res = await apolloClient.query<{ getPossibleChoice: ProductInfo[] }>({
    query: GET_POSSIBLE_CHOICE,
    fetchPolicy: 'no-cache',
    variables: { periodId },
  });
  const sortedProductsByProductLine = res.data.getPossibleChoice.sort((a, b) => {
    return sortByProductLineId(a.productLineId, b.productLineId);
  });
  return sortedProductsByProductLine;
}

export async function getEmployeeClasses(groupId: string, periodId: string): Promise<EeClass[]> {
  const res = await apolloClient.query<{ getEmployeeClasses: EeClass[] }>({
    query: GET_EMPLOYEE_CLASSES,
    variables: { groupId, periodId },
    fetchPolicy: 'no-cache',
  });
  return res.data.getEmployeeClasses.sort((a, b) => (a.type > b.type ? -1 : 1));
}

export async function createEmployeesForGroup(
  groupId: string,
  addOnMethod: SubscriberAddOnMethod,
  employees: Record<string, string>[],
): Promise<boolean> {
  return (
    await apolloClient.mutate<{ createEmployeesForGroup: boolean }>({
      mutation: CREATE_EMPLOYEES_FOR_GROUP,
      variables: { groupId, addOnMethod, employees },
    })
  ).data!.createEmployeesForGroup;
}

export async function addFullEmployeeToGroup(groupId: string, employee: Record<string, string>): Promise<string> {
  return (
    await apolloClient.mutate<{ addFullEmployeeToGroup: { id: string } }>({
      mutation: ADD_FULL_EMPLOYEE_TO_GROUP,
      variables: { groupId, employee },
    })
  ).data!.addFullEmployeeToGroup.id;
}

export async function remindCompleteProfile(subId: string): Promise<void> {
  await apolloClient.query<{ remindCompleteProfile: boolean }>({
    query: REMIND_COMPLETE_PROFILE,
    variables: { subId },
  });
}

export async function inviteAll(groupId: string): Promise<boolean> {
  return (
    await apolloClient.mutate<{ inviteAll: boolean }>({
      mutation: INVITE_ALL,
      variables: { groupId },
    })
  ).data!.inviteAll;
}

export async function closeQuotingTasks(groupId: string): Promise<boolean> {
  return (
    await apolloClient.mutate<{ closeQuotingTasks: boolean }>({
      mutation: CLOSE_QUOTING_TASKS,
      variables: { groupId },
    })
  ).data!.closeQuotingTasks;
}

export async function applyNoConditions(subId: string): Promise<void> {
  await apolloClient.mutate<{ applyNoConditions: void }>({
    mutation: APPLY_NO_CONDITIONS,
    variables: { subId },
  });
}

export async function saveGeneralIMQAnswers(subId: string, answers: Record<string, Primitive>): Promise<void> {
  await apolloClient.mutate<{ saveGeneralIMQAnswers: void }>({
    mutation: SAVE_GENERAL_IMQ_ANSWERS,
    variables: { subId, answers },
  });
}

export async function saveIMQCategoryAnswers(
  subId: string,
  category: string,
  answers: Record<string, Primitive>,
): Promise<void> {
  await apolloClient.mutate<{ saveIMQCategoryAnswers: void }>({
    mutation: SAVE_IMQ_CATEGORY_ANSWERS,
    variables: { subId, category, answers },
  });
}

export async function saveIMQMedicalConditionAnswers(
  subId: string,
  category: string,
  condition: string,
  memberId: string,
  answers: Record<string, Primitive | string[]>,
): Promise<{ id: string; imqAnswers: IMQAnswers }> {
  const res = await apolloClient.mutate<{ saveIMQMedicalConditionAnswers: SubscriberIMQ }>({
    mutation: SAVE_IMQ_MEDICAL_CONDITION_ANSWERS,
    variables: { subId, category, condition, memberId, answers },
  });

  return {
    id: res.data!.saveIMQMedicalConditionAnswers.id,
    imqAnswers: res.data!.saveIMQMedicalConditionAnswers.imqAnswers!,
  };
}

export async function removeIMQMedicalConditionMember(
  subId: string,
  category: string,
  condition: string,
  memberId: string,
): Promise<{ id: string; imqAnswers: IMQAnswers }> {
  const res = await apolloClient.mutate<{ removeIMQMedicalConditionMember: SubscriberIMQ }>({
    mutation: REMOVE_IMQ_MEDICAL_CONDITION_MEMBER,
    variables: { subId, category, condition, memberId },
  });

  return {
    id: res.data!.removeIMQMedicalConditionMember.id,
    imqAnswers: res.data!.removeIMQMedicalConditionMember.imqAnswers!,
  };
}
